import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { ILmsRecommendationDecision, TLmsContentPreview, TLmsSuggestion } from "@/types/lms";

/** The three ids that name one course or path. A suggestion carries them, and so does a
 *  kept row on a project's Delivery tab (NG-1928), which previews the same content through
 *  the same cache. */
type TLmsContentRef = Pick<TLmsSuggestion, "connection_id" | "kind" | "external_id">;

/** Suggested LMS content for an intake request (plan §6.7, use case #18, NG-676).
 *
 *  **Dismissal is optimistic; accepting is not.** They are not the same kind of write:
 *  dismissing removes a row the reader has just judged wrong, and leaving it on screen
 *  while a round trip finishes is the one thing that makes the panel feel unresponsive.
 *  Accepting changes what the row *says* — it becomes a decision on the request, and it
 *  reorders — so painting the outcome before the server agrees would show a state that
 *  might not survive.
 *
 *  Keyed by submission id: this panel is reachable from several requests in one session,
 *  and a single array would show the previous request's suggestions for the frame before
 *  the fetch resolves.
 */
export class SubmissionLmsRecommendationStore {
  suggestionsBySubmission: Record<string, TLmsSuggestion[]> = {};
  /** The title the backend scored, per request. Worth surfacing: it is not a column —
   *  it comes from `response_data["request_name"]` and falls back to the form's name —
   *  so a reader seeing odd suggestions can tell which string produced them. */
  scoredTitleBySubmission: Record<string, string> = {};
  isLoading = false;
  /** Kept apart from "no suggestions", because on screen those look identical and only
   *  one of them is a bug. */
  loadFailed = false;
  decidingKey: string | null = null;
  /** The Preview dialog's stored facts, per suggestion key (NG-1858). Cached for the
   *  session: stepping back and forth through the dialog should not refetch, and none of
   *  it changes faster than the nightly pull that feeds it. */
  previewByKey: Record<string, TLmsContentPreview> = {};
  /** Keys whose preview fetch is in flight, so the rail can show a skeleton. */
  previewLoadingKeys: Record<string, true> = {};

  private rootStore: RootStore;
  /** Latest-wins: a slow fetch for one request must never overwrite a later one. */
  private requestSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  suggestionsFor(submissionId: string): TLmsSuggestion[] {
    return this.suggestionsBySubmission[submissionId] ?? [];
  }

  scoredTitleFor(submissionId: string): string {
    return this.scoredTitleBySubmission[submissionId] ?? "";
  }

  /** A stable key for one suggestion, since a suggestion has no id of its own — it is
   *  computed, not stored, and the vendor's id is only unique within a connection. */
  keyFor(suggestion: TLmsContentRef): string {
    return `${suggestion.connection_id}:${suggestion.kind}:${suggestion.external_id}`;
  }

  async fetchSuggestions(slug: string, submissionId: string): Promise<void> {
    const seq = ++this.requestSeq;
    runInAction(() => {
      this.isLoading = true;
      this.loadFailed = false;
    });
    try {
      const payload = await this.rootStore.lmsRecommendationService.listSuggestions(
        slug,
        submissionId,
      );
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.suggestionsBySubmission[submissionId] = payload.results ?? [];
        this.scoredTitleBySubmission[submissionId] = payload.scored_title ?? "";
      });
    } catch (error) {
      // A failed fetch must not read as "no suggestions": the panel says so instead.
      console.error("[submission-lms-recommendation] suggestion fetch failed", {
        slug,
        submissionId,
        error,
      });
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.loadFailed = true;
        });
      }
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  /** Dismiss one suggestion. Optimistic: the row goes now and comes back on failure. */
  async dismiss(slug: string, submissionId: string, suggestion: TLmsSuggestion): Promise<void> {
    const key = this.keyFor(suggestion);
    const before = this.suggestionsFor(submissionId);
    runInAction(() => {
      this.suggestionsBySubmission[submissionId] = before.filter(
        (item) => this.keyFor(item) !== key,
      );
      this.decidingKey = key;
    });
    try {
      await this.decide(slug, submissionId, suggestion, "dismissed");
    } catch (error) {
      console.error("[submission-lms-recommendation] dismiss failed", {
        slug,
        submissionId,
        suggestion: key,
        error,
      });
      // Put it back rather than leaving it hidden: a dismissal the server never stored
      // would reappear on the next reader's screen anyway, and silently disagreeing with
      // the server is worse than showing the row again.
      runInAction(() => {
        this.suggestionsBySubmission[submissionId] = before;
      });
    } finally {
      runInAction(() => {
        this.decidingKey = null;
      });
    }
  }

  /** Accept one suggestion, then refetch so the marking and the reorder come from the
   *  server rather than from a guess about how it sorts. */
  async accept(slug: string, submissionId: string, suggestion: TLmsSuggestion): Promise<void> {
    const key = this.keyFor(suggestion);
    runInAction(() => {
      this.decidingKey = key;
    });
    try {
      await this.decide(slug, submissionId, suggestion, "accepted");
      await this.fetchSuggestions(slug, submissionId);
    } catch (error) {
      console.error("[submission-lms-recommendation] accept failed", {
        slug,
        submissionId,
        suggestion: key,
        error,
      });
    } finally {
      runInAction(() => {
        this.decidingKey = null;
      });
    }
  }

  previewFor(suggestion: TLmsContentRef): TLmsContentPreview | null {
    return this.previewByKey[this.keyFor(suggestion)] ?? null;
  }

  isPreviewLoading(suggestion: TLmsContentRef): boolean {
    return Boolean(this.previewLoadingKeys[this.keyFor(suggestion)]);
  }

  /** Load what Cognota stores about one suggestion, once per session.
   *
   *  A failure is a degradation, not a broken action: the dialog still shows everything
   *  the suggestion row carried, and only the rail's stored-facts sections go missing. So
   *  it warns rather than errors, and a later open tries again.
   */
  async fetchPreview(
    slug: string,
    submissionId: string,
    suggestion: TLmsContentRef,
  ): Promise<void> {
    const key = this.keyFor(suggestion);
    if (this.previewByKey[key] || this.previewLoadingKeys[key]) return;
    // A plain copy of the three ids, read before the await: the service gets values, not
    // the observable row, which a refetch may replace while the request is in flight.
    const target = {
      connection_id: suggestion.connection_id,
      kind: suggestion.kind,
      external_id: suggestion.external_id,
    };
    runInAction(() => {
      this.previewLoadingKeys[key] = true;
    });
    try {
      const preview = await this.rootStore.lmsRecommendationService.getPreview(
        slug,
        submissionId,
        target,
      );
      if (preview) {
        runInAction(() => {
          this.previewByKey[key] = preview;
        });
      }
    } catch (error) {
      console.warn("[submission-lms-recommendation] preview fetch failed", {
        slug,
        submissionId,
        suggestion: key,
        error,
      });
    } finally {
      runInAction(() => {
        delete this.previewLoadingKeys[key];
      });
    }
  }

  private async decide(
    slug: string,
    submissionId: string,
    suggestion: TLmsSuggestion,
    decision: ILmsRecommendationDecision["decision"],
  ): Promise<void> {
    await this.rootStore.lmsRecommendationService.decide(slug, submissionId, {
      connection_id: suggestion.connection_id,
      kind: suggestion.kind,
      external_id: suggestion.external_id,
      decision,
      score: suggestion.score,
    });
  }
}
